/* eslint-disable react-native/no-inline-styles */
import React from "react"
import { useTranslation } from "react-i18next"
import { connect } from "react-redux"

import * as Geo from "@/base/geo"
import { isNumberValid } from "@/base/type-guards"
import {
  BASE_SWATH_FILL_OPACITY,
  SWATH_OUTLINE_STROKE_WEIGHT,
} from "@/constants/google-maps"
import * as Models from "@/redux/models"

import type { ApplicationType } from "@/base/sensor-configurations"
import type {
  MapElementColoration,
  ReelRunVariant,
} from "@/redux/reel-runs.reducer"
import { getSwathGeometryCachedByReelRunPropertiesFromState } from "@/redux/reel-runs.reducer"
import type { RootState } from "@/redux/root.reducer"
import { COLORS, RADII, getFontName } from "@/theme"
import { OverlayView, Polygon } from "@react-google-maps/api"
import * as SelectedRunContext from "./SelectedRunContext"

interface OwnProps {
  baseFillOpacity?: number | undefined
  coloration: MapElementColoration | undefined
  isHidden?: boolean
  isPressable?: boolean
  onPress?: ((e: google.maps.MapMouseEvent) => void) | undefined
  showCompletionText: boolean
  showOutlinePolygon: boolean
  showProgressPolygon: boolean
}

interface ReelRunPolygonProps extends OwnProps {
  applicationType?: ApplicationType | null | undefined
  centerLatLng: google.maps.LatLngLiteral | undefined
  completionPct: number | null | undefined
  isPressable: boolean
  onPress: ((e: google.maps.MapMouseEvent) => void) | undefined
  outlinePath: google.maps.LatLngLiteral[] | undefined
  progressPath: google.maps.LatLngLiteral[] | undefined
}

/**
 * We can reuse this mapState function for both historical and active reel runs;
 * which exist in different locations in the app state
 */
function makeConnector(variant: ReelRunVariant) {
  return connect(
    (
      state: RootState,
      {
        azimuthOverride,
        coloration,
        isPressable,
        onPress,
        reelRunId,
        showCompletionText,
        showOutlinePolygon,
        showProgressPolygon,
      }: Models.ReelRunIdProps &
        OwnProps & {
          azimuthOverride?: number | undefined
        },
    ): ReelRunPolygonProps => {
      const geometry = getSwathGeometryCachedByReelRunPropertiesFromState(
        state,
        {
          azimuthOverride,
          id: reelRunId,
          variant,
        },
      )
      const runData =
        variant === "active"
          ? Models.reelRunsActive.selectById(state, reelRunId)
          : Models.fieldIrrigationHistoryRuns.selectById(state, reelRunId)

      return {
        applicationType: runData?.applicationType,
        centerLatLng: geometry?.centerLatLng?.web,
        coloration,
        completionPct: geometry?.completionPct,
        isPressable: isPressable !== false,
        onPress,
        outlinePath: geometry?.outlinePath?.web,
        progressPath: geometry?.progressPath?.web,
        showCompletionText,
        showOutlinePolygon,
        showProgressPolygon,
      }
    },
  )
}

export function ReelRunPolygonWeb({
  applicationType,
  baseFillOpacity,
  centerLatLng,
  coloration,
  completionPct,
  isHidden = false,
  isPressable = true,
  onPress,
  outlinePath,
  progressPath,
  showCompletionText = false,
  showOutlinePolygon = true,
  showProgressPolygon = true,
}: ReelRunPolygonProps): React.JSX.Element | null {
  const outlineOptions = React.useMemo((): google.maps.PolygonOptions => {
    let strokeWeight = SWATH_OUTLINE_STROKE_WEIGHT
    if (coloration === "highlight") {
      strokeWeight += 2
    } else if (coloration === "mute") {
      strokeWeight -= 0.8
    }
    return {
      clickable: isPressable,
      fillColor: "transparent",
      fillOpacity: 0,
      strokeColor:
        applicationType === "manure"
          ? COLORS.$reelRunSwathManure.outline
          : COLORS.$reelRunSwath.outline,
      strokeWeight,
      visible: !isHidden && showOutlinePolygon,
    }
  }, [applicationType, coloration, isHidden, isPressable, showOutlinePolygon])

  const progressOptions = React.useMemo((): google.maps.PolygonOptions => {
    let fillOpacity = baseFillOpacity ?? BASE_SWATH_FILL_OPACITY
    if (coloration === "mute") {
      fillOpacity /= 2
    } else if (coloration === "highlight") {
      fillOpacity += 0.2
    }
    return {
      clickable: isPressable,
      fillColor:
        applicationType === "manure"
          ? COLORS.$reelRunSwathManure.fill
          : COLORS.$reelRunSwath.fill,
      fillOpacity,
      strokeOpacity: 0,
      strokeWeight: 0,
      visible: !isHidden && showProgressPolygon,
    }
  }, [
    applicationType,
    baseFillOpacity,
    coloration,
    isHidden,
    isPressable,
    showProgressPolygon,
  ])

  const handlePress = isPressable ? onPress : undefined
  const { t } = useTranslation()

  return (
    <React.Fragment>
      {centerLatLng &&
      showProgressPolygon &&
      isNumberValid(completionPct) &&
      showCompletionText ? (
        <OverlayView
          mapPaneName={"overlayMouseTarget"}
          position={
            Geo.point(centerLatLng)
              ?.project({ distanceMeters: -40 })
              ?.toGmaps() ?? centerLatLng
          }
        >
          <div
            style={{
              alignItems: "center",
              backgroundColor: COLORS.$white,
              borderRadius: RADII.$default,
              display: "flex",
              height: 20,
              justifyContent: "center",
              opacity: 0.8,
              paddingLeft: 10,
              paddingRight: 10,
              pointerEvents: "none",
              width: 24,
            }}
          >
            <p
              color={COLORS.$textDark.primary}
              style={{
                fontFamily: getFontName("Poppins_500Medium"),
                fontSize: 14,
              }}
            >
              {t("percentWithVal", { val: completionPct })}
            </p>
          </div>
        </OverlayView>
      ) : null}
      {showOutlinePolygon && outlinePath ? (
        <Polygon
          options={outlineOptions}
          path={outlinePath}
          onClick={handlePress}
        />
      ) : null}
      {showProgressPolygon ? (
        <Polygon
          options={progressOptions}
          path={progressPath}
          onClick={handlePress}
        />
      ) : null}
    </React.Fragment>
  )
}

export const ReelRunPolygonActiveWeb =
  makeConnector("active")(ReelRunPolygonWeb)
export const ReelRunPolygonHistoricalWeb =
  makeConnector("historical")(ReelRunPolygonWeb)

export function SetRunDirectionPolygon(): React.JSX.Element | null {
  const { actionName, centerLatLng, runData, swathGeometry } =
    SelectedRunContext.useSelectedReelRunContext()
  if (actionName !== "setRunDirection") {
    return null
  }

  if (swathGeometry?.progressPath && swathGeometry.outlinePath) {
    return (
      <ReelRunPolygonWeb
        showOutlinePolygon
        showProgressPolygon
        applicationType={runData?.applicationType}
        centerLatLng={centerLatLng?.web}
        coloration={"highlight"}
        completionPct={runData?.completionPct}
        isHidden={false}
        isPressable={false}
        outlinePath={swathGeometry.outlinePath.web}
        progressPath={swathGeometry.progressPath.web}
        showCompletionText={false}
        onPress={undefined}
      />
    )
  }
  return null
}
